import React from 'react';
import {CoreDI, IEventManagerWithAsync, lazyInject, ScreenManager, ScreenParameters} from '@gongxigames/framework/core';
import {GlobalEventsMap} from '@gongxigames/framework/slot/events';
import {CheatPluginView} from '../../CheatPluginView';
import {CheatSessionController} from './CheatSessionController';
import {DisplayObjectList} from '../../components/DisplayObjectList';
import {InputField} from './InputField';
import {ButtonWithTitle} from '../../components/ButtonWithTitle';
import {ScrollComponent} from '../../components/ScrollContainer';
import {SessionView} from './SessionView';

type CheatSessionPageState = {
    sessionName: string;
    sessionRounds: number;
    width: number;
    height: number;
};

export class CheatSessionView extends CheatPluginView<CheatSessionController, {}, CheatSessionPageState> {
    @lazyInject(CoreDI.IEventManagerWithAsync)
    protected eventManager!: IEventManagerWithAsync<GlobalEventsMap>;

    @lazyInject(CoreDI.ScreenManager)
    protected fullscreenManager!: ScreenManager;

    constructor(props: any) {
        super(props);

        this.state = {
            width: this.fullscreenManager.width,
            height: this.fullscreenManager.height,
            sessionName: '',
            sessionRounds: 0
        };
    }

    public componentDidMount(): void {
        const {store} = this.controller;

        store.on('sessions', () => this.forceUpdate(), this);
        store.on('currentSessionID', () => this.forceUpdate(), this);
        this.eventManager.on('General.Screen.RESIZED', this.onResized.bind(this), this);
    }

    public componentWillUnmount(): void {
        this.controller.store.off('sessions', this);
        this.controller.store.off('currentSessionID', this);
        this.eventManager.off('General.Screen.RESIZED', this);
    }

    protected onResized({width, height}: ScreenParameters): void {
        this.setState({width, height});
    }

    protected saveSession(): void {
        const {sessionName, sessionRounds} = this.state;

        if (sessionName && sessionRounds) {
            this.controller.save(sessionName, sessionRounds);
            this.setState({sessionRounds: 0, sessionName: ''});
        }
    }

    protected onChangeSessionName(sessionName: string): void {
        this.setState({sessionName});
    }

    protected onChangeSessionRounds(rounds: string): void {
        this.setState({sessionRounds: Number(rounds)});
    }

    protected onSelectSession(id: number): void {
        this.controller.set(id);
    }

    protected onDeleteSession(id: number): void {
        this.controller.delete(id);
    }

    protected clearSessionSelected(): void {
        this.controller.unselect();
    }

    public render(): JSX.Element {
        const {width, height, sessionName, sessionRounds} = this.state;
        const {sessions, currentSessionID} = this.controller.store.data;

        return (
            <DisplayObjectList y={200} direction={'column'} gapBetweenChildren={20}>
                <DisplayObjectList direction={'row'} gapBetweenChildren={20} wrap={width - 100}>
                    <InputField
                        placeholder={'Enter session name'}
                        title={'Session Name'}
                        onChange={this.onChangeSessionName.bind(this)}
                        value={sessionName}
                    />
                    <InputField
                        placeholder={'Enter session rounds'}
                        title={'Session rounds amount'}
                        onChange={this.onChangeSessionRounds.bind(this)}
                        value={sessionRounds.toString()}
                    />
                    <ButtonWithTitle title={'Save session'} fontSize={80} onClick={this.saveSession.bind(this)} height={150} width={200} />
                </DisplayObjectList>
                <ButtonWithTitle title={'Clear session'} fontSize={80}
                    onClick={this.clearSessionSelected.bind(this)}
                    height={150}
                    width={500}
                />
                <ScrollComponent boundsWidth={width - 40} boundsHeight={height - 1050} >
                    <DisplayObjectList gapBetweenChildren={20}>
                        {sessions.map(({ID = -1, session_name, rounds_amount}) => {
                            return <SessionView
                                key={ID}
                                id={ID}
                                selected={ID === currentSessionID}
                                rounds={rounds_amount}
                                name={session_name}
                                onSelect={this.onSelectSession.bind(this)}
                                onDelete={this.onDeleteSession.bind(this)}
                                width={width - 60}
                            />;
                        } )}
                    </DisplayObjectList>
                </ScrollComponent>
            </DisplayObjectList>
        );
    }
}
